import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';

const CALCULATION_STAGES = [
  "Analyzing historical market data...",
  "Calculating risk scenarios...",
  "Projecting future returns...",
  "Optimizing investment strategies...",
  "Generating visualization models...",
  "Finalizing calculations..."
];

const MIN_DISPLAY_TIME = 6000; // 6 seconds minimum display time
const MAX_LOADING_TIME = 30000; // 30 seconds maximum loading time

interface LoadingOverlayProps {
  onSkip?: () => void;
}

export function LoadingOverlay({ onSkip }: LoadingOverlayProps) {
  const [currentStage, setCurrentStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [canClose, setCanClose] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    let frameId: number;
    let maxTimeout: NodeJS.Timeout;

    // Set maximum loading time timeout
    maxTimeout = setTimeout(() => {
      setTimedOut(true);
      setCanClose(true);
      if (onSkip) {
        onSkip();
      }
    }, MAX_LOADING_TIME);

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const rawProgress = Math.min(elapsed / MIN_DISPLAY_TIME, 1);
      
      // Use easeInOutCubic for smoother animation
      const easeInOutCubic = (t: number) => {
        return t < 0.5
          ? 4 * t * t * t
          : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };
      
      const easedProgress = easeInOutCubic(rawProgress);
      setProgress(easedProgress);
      
      // Update stage based on progress
      const stageIndex = Math.min(
        Math.floor(easedProgress * CALCULATION_STAGES.length),
        CALCULATION_STAGES.length - 1
      );
      setCurrentStage(stageIndex);

      if (elapsed < MIN_DISPLAY_TIME) {
        frameId = requestAnimationFrame(animate);
      } else {
        setCanClose(true);
      }
    };

    frameId = requestAnimationFrame(animate);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      if (maxTimeout) {
        clearTimeout(maxTimeout);
      }
    };
  }, [startTime, onSkip]);

  const handleSkip = () => {
    if (canClose && onSkip) {
      onSkip();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 relative">
        {onSkip && (
          <button
            onClick={handleSkip}
            disabled={!canClose}
            className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
              canClose 
                ? 'text-gray-400 hover:text-gray-600 hover:bg-gray-100' 
                : 'text-gray-300 cursor-not-allowed'
            }`}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Skip loading</span>
          </button>
        )}
        
        <div className="p-8">
          <div className="flex flex-col items-center space-y-6">
            {timedOut ? (
              <>
                <div className="w-16 h-16 flex items-center justify-center">
                  <AlertTriangle className="h-12 w-12 text-amber-500" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Calculation Taking Longer Than Expected
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    The calculation is taking longer than usual. This could be due to high server load or network issues.
                  </p>
                  <button
                    onClick={handleSkip}
                    className="inline-flex items-center px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <div className="w-full text-center">
                  <div className="h-8 mb-4">
                    <p className="text-lg font-medium text-gray-900">
                      {CALCULATION_STAGES[currentStage]}
                    </p>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mb-1">
                    <div 
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-200 ease-out"
                      style={{ width: `${progress * 100}%` }}
                    />
                  </div>
                  
                  <p className="text-sm text-gray-500">
                    {Math.round(progress * 100)}% complete
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}